import {
  type ButtonHTMLAttributes,
  forwardRef,
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { cn } from '../cn.js';

export type ButtonVariant = 'primary' | 'resonance' | 'ghost' | 'quiet';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * Pointer-tracking magnetic micro-interaction. Only meaningful on
   * `variant="resonance"`. The button translates ≤ 3px toward the cursor
   * within a 64px radius, dampened, with a 160ms ease-out reset on leave.
   * No-op under `prefers-reduced-motion: reduce`. Pointer-only — keyboard
   * focus and tab order are unaffected.
   */
  magnetic?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-surface-3 border border-hairline-strong text-ink-0 hover:bg-surface-2',
  resonance:
    'border border-aurora/40 bg-[color-mix(in_oklab,var(--aurora)_18%,var(--surface-2))] text-ink-0 shadow-glow-aurora hover:shadow-[0_0_0_1px_color-mix(in_oklab,var(--aurora)_50%,transparent),0_12px_44px_color-mix(in_oklab,var(--aurora)_24%,transparent)]',
  ghost: 'border border-hairline bg-transparent text-ink-0 hover:bg-surface-3',
  quiet: 'border border-transparent bg-transparent text-ink-1 hover:text-ink-0',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2.5 text-sm',
  lg: 'px-6 py-3 text-base',
};

const baseClasses = cn(
  'inline-flex items-center justify-center gap-2 rounded-md font-medium',
  'transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurora/60 focus-visible:ring-offset-2 focus-visible:ring-offset-abyss',
);

/**
 * Class composer for button styling — exported so a TanStack `<Link>` can render
 * as an `<a>` with button visuals without nesting interactive elements.
 * Use directly on the Link: `<Link className={buttonClasses({ variant: 'resonance' })}>`.
 */
export function buttonClasses({
  variant = 'primary',
  size = 'md',
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
} = {}): string {
  return cn(baseClasses, variantClasses[variant], sizeClasses[size], className);
}

const MAGNETIC_RADIUS = 64;
const MAGNETIC_MAX_TRANSLATE = 3;
const MAGNETIC_DAMPING = 0.18;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Qi buttons — primary / resonance / ghost / quiet variants from the design system.
 * Defaults to `primary md` with cinematic ease on the hover transition. Pass
 * `magnetic` on the resonance variant to enable pointer-tracking nudge.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', className, type = 'button', magnetic = false, ...rest },
  ref,
) {
  const innerRef = useRef<HTMLButtonElement | null>(null);

  const setRefs = useCallback(
    (node: HTMLButtonElement | null) => {
      innerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as MutableRefObject<HTMLButtonElement | null>).current = node;
    },
    [ref],
  );

  useEffect(() => {
    if (!magnetic || variant !== 'resonance') return;
    if (prefersReducedMotion()) return;
    const el = innerRef.current;
    if (!el || typeof window === 'undefined') return;

    let raf = 0;
    function onMove(e: PointerEvent) {
      if (e.pointerType === 'touch') return;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist > MAGNETIC_RADIUS) {
        reset();
        return;
      }
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!el) return;
        const tx = Math.max(
          -MAGNETIC_MAX_TRANSLATE,
          Math.min(MAGNETIC_MAX_TRANSLATE, dx * MAGNETIC_DAMPING),
        );
        const ty = Math.max(
          -MAGNETIC_MAX_TRANSLATE,
          Math.min(MAGNETIC_MAX_TRANSLATE, dy * MAGNETIC_DAMPING),
        );
        el.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
      });
    }
    function reset() {
      cancelAnimationFrame(raf);
      if (!el) return;
      el.style.transform = '';
    }

    window.addEventListener('pointermove', onMove, { passive: true });
    el.addEventListener('pointerleave', reset);
    el.addEventListener('blur', reset);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', reset);
      el.removeEventListener('blur', reset);
      reset();
    };
  }, [magnetic, variant]);

  return (
    <button
      ref={setRefs}
      type={type}
      className={buttonClasses({ variant, size, className })}
      style={{
        transitionProperty: 'transform, color, background-color, border-color, box-shadow',
      }}
      {...rest}
    />
  );
});
